import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {BehaviorSubject, distinct, Observable} from 'rxjs';
import environment from '../../environment';
import {CommonService} from './common.service';

@Injectable()
export class AuthService {
  private sessionExpiryTimeoutId: number | null = null;
  private isAuth: BehaviorSubject<boolean | string | null> =
    new BehaviorSubject(false);
  public isAuthenticated: Observable<boolean | string | null> =
    this.isAuth.pipe(distinct());

  constructor(
    public jwtHelper: JwtHelperService,
    public router: Router,
    private commonService: CommonService
  ) {
    this.updateAuthState();
  }

  public isLoggedIn() {
    return Boolean(this.getToken());
  }

  public tokenChanged() {
    const loggedIn = this.updateAuthState();
    if (environment.settings.verboseLogging) {
      console.log('[AuthService] tokenChanged ->', loggedIn);
    }
  }

  public applyStandardSession(
    apiToken: string,
    refreshToken?: string | null
  ): void {
    this.setCurrentSession(apiToken, refreshToken);
    this.tokenChanged();
  }

  getToken() {
    const token = localStorage.getItem('partnerchoice_api_token');
    if (token && token !== 'null' && this.jwtHelper.isTokenExpired(token)) {
      if (environment.settings.verboseLogging) {
        console.warn(
          '[AuthService] Stored AWS token expired:',
          this.maskToken(token)
        );
      }
      this.clearStoredTokens();
      this.clearSessionExpiryTimer();
      this.isAuth.next(false);
      return null;
    }

    return token;
  }

  logout(reason?: string) {
    if (environment.settings.verboseLogging) {
      console.warn('[AuthService] logout', {
        reason,
        path: globalThis.location.pathname,
        awsToken: this.maskToken(
          localStorage.getItem('partnerchoice_api_token')
        ),
      });
    }
    this.clearStoredTokens();
    this.clearSessionExpiryTimer();
    this.isAuth.next(false);
    this.commonService.navigate('/Login');
    setTimeout(() => globalThis.location.reload(), 0);
  }

  public setAuthenticated(value: boolean) {
    if (environment.settings.verboseLogging) {
      console.log('[AuthService] setAuthenticated ->', value);
    }
    this.isAuth.next(value);
  }

  private maskToken(token: string | null): string {
    if (!token) {
      return 'null';
    }

    const start = token.slice(0, 8);
    const end = token.slice(-6);
    return `${start}...${end} (len ${token.length})`;
  }

  private updateAuthState(): boolean {
    const token = this.getToken();
    const loggedIn = Boolean(token);

    this.scheduleSessionExpiry(token);
    this.isAuth.next(loggedIn);

    return loggedIn;
  }

  private scheduleSessionExpiry(token: string | null): void {
    this.clearSessionExpiryTimer();

    if (!token || token === 'null') {
      return;
    }

    const expirationDate = this.jwtHelper.getTokenExpirationDate(token);

    if (!expirationDate) {
      return;
    }

    const delayMs = expirationDate.getTime() - Date.now();

    if (delayMs <= 0) {
      this.clearStoredTokens();
      this.isAuth.next(false);
      return;
    }

    this.sessionExpiryTimeoutId = globalThis.setTimeout(() => {
      const activeToken = localStorage.getItem('partnerchoice_api_token');

      if (!activeToken || activeToken !== token) {
        return;
      }

      this.logout('token-expired');
    }, delayMs);
  }

  private clearStoredTokens(): void {
    localStorage.removeItem('partnerchoice_api_token');
    localStorage.removeItem('partnerchoice_api_refresh_token');
  }

  private clearSessionExpiryTimer(): void {
    if (this.sessionExpiryTimeoutId !== null) {
      globalThis.clearTimeout(this.sessionExpiryTimeoutId);
      this.sessionExpiryTimeoutId = null;
    }
  }

  private setCurrentSession(
    apiToken: string,
    refreshToken?: string | null
  ): void {
    localStorage.setItem('partnerchoice_api_token', apiToken);

    if (refreshToken) {
      localStorage.setItem('partnerchoice_api_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('partnerchoice_api_refresh_token');
    }
  }
}

export function tokenGetter(request?: any) {
  if (request && request.url.includes(environment.settings.registeredAwsUrl)) {
    return localStorage.getItem('partnerchoice_api_token');
  }
  return null;
}
